import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useDroppable, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { cn } from "@/lib/utils"
import { collectVars, GLOBAL_VARS, validate } from "@/lib/agents/playbooks"
import { AiItemCard } from "./aicard"
import { FlowSheet } from "./flowsheet"
import { PlaybookHeader } from "./header"
import { ItemCard } from "./itemcard"
import { LanguageContext } from "./language"

export type FlowGroups = Playbook["flows"]

export interface CardProps {
  item: PlaybookItem
  vars: string[]
  groups: FlowGroups
  index: number
  issues: PlaybookIssue[]
  onUpdate: (patch: Partial<PlaybookItem>) => void
  onRemove: () => void
}

interface Props {
  playbook: Playbook
  cursor?: PlaybookCursor | null
  focus?: PlaybookCursor
  aside?: ReactNode
  onChange: (playbook: Playbook) => void
  onSave?: () => void
}

function blankItem(kind: "item" | "ai"): PlaybookItem {
  return kind === "ai" ? { id: "", type: "ai" } : { id: "" }
}

interface View {
  flow?: string
  scroll?: number
}

function readView(key: string): View {
  try { return JSON.parse(localStorage.getItem(key) || "{}") } catch { return {} }
}

function writeView(key: string, patch: View) {
  localStorage.setItem(key, JSON.stringify({ ...readView(key), ...patch }))
}

export function PlaybookItems({ playbook, cursor, focus, aside, onChange, onSave }: Props) {
  const groups = playbook.flows
  const viewKey = `playbook-editor:${playbook.id}`
  const [flow, setFlow] = useState(() => { const saved = readView(viewKey).flow; return saved && groups[saved] ? saved : "main" })
  const [pendingScrollIndex, setPendingScrollIndex] = useState<number | null>(null)
  const [flash, setFlash] = useState<PlaybookCursor>()
  const [language, setLanguage] = useState<string>()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const vars = useMemo(() => [...collectVars(playbook), ...GLOBAL_VARS], [playbook])
  const issues = useMemo(() => validate(playbook), [playbook])
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {}),
  )

  useEffect(() => {
    if (pendingScrollIndex === null) return
    scrollContainerRef.current?.querySelector(`[data-item-index="${pendingScrollIndex}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    setPendingScrollIndex(null)
  }, [pendingScrollIndex, groups[flow]?.items])

  useEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = readView(viewKey).scroll ?? 0
  }, [])

  const followed = useRef(false)
  useEffect(() => {
    if (!cursor) return
    if (!followed.current) { followed.current = true; return }
    follow(cursor)
  }, [cursor?.flow, cursor?.item])

  useEffect(() => {
    if (!focus || !follow(focus)) return
    setFlash(focus)
    const timer = setTimeout(() => setFlash(undefined), 1500)
    return () => clearTimeout(timer)
  }, [focus])

  function follow(target: PlaybookCursor) {
    const index = groups[target.flow]?.items.findIndex(i => i.id === target.item) ?? -1
    if (index < 0) return false
    if (target.flow !== flow) changeFlow(target.flow)
    setPendingScrollIndex(index)
    return true
  }

  function changeFlow(next: string) {
    setFlow(next)
    writeView(viewKey, { flow: next })
  }

  function setItems(items: PlaybookItem[]) {
    onChange({ ...playbook, flows: { ...playbook.flows, [flow]: { ...playbook.flows[flow], items } } })
  }

  function updateItem(index: number, patch: Partial<PlaybookItem>) {
    setItems(groups[flow].items.map((item, i) => i === index ? { ...item, ...patch } : item))
  }

  function appendItem(kind: "item" | "ai") {
    setPendingScrollIndex(groups[flow].items.length)
    setItems([...groups[flow].items, blankItem(kind)])
  }

  function insertItem(index: number, kind: "item" | "ai") {
    const items = [...groups[flow].items]
    items.splice(index, 0, blankItem(kind))
    setItems(items)
  }

  function removeItem(index: number) {
    setItems(groups[flow].items.filter((_, i) => i !== index))
  }

  function moveItem(from: number, to: number) {
    setItems(arrayMove(groups[flow].items, from, to > from ? to - 1 : to))
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return
    const gapIndex = Number(String(over.id).slice("gap-".length))
    if (active.id === "new-ai" || active.id === "new-item") insertItem(gapIndex, active.id === "new-ai" ? "ai" : "item")
    else moveItem(Number(active.id), gapIndex)
  }

  return <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
    <LanguageContext.Provider value={language}>
      <div className="flex flex-col flex-1 min-h-0">
        <PlaybookHeader playbook={playbook} flow={flow} issues={issues} onChange={onChange} onSelectFlow={changeFlow} onAppend={appendItem}
          language={language} onLanguage={setLanguage} onSave={onSave} />
        <div className="flex flex-1 min-h-0 gap-4">
          <div className="flex flex-col w-2/3 min-h-0 pt-2">
            <div className="px-4"><FlowSheet playbook={playbook} flow={flow} onChange={onChange} onSelectFlow={changeFlow} /></div>
            <div ref={scrollContainerRef} onScroll={e => writeView(viewKey, { scroll: e.currentTarget.scrollTop })} className="flex-1 min-h-0 overflow-y-auto p-4 pt-2">
              <ItemGroup items={groups[flow].items} vars={vars} groups={groups} issues={issues.filter(i => i.flow === flow)} activeId={cursor?.flow === flow ? cursor.item : undefined}
                flashId={flash?.flow === flow ? flash.item : undefined} onUpdate={updateItem} onRemove={removeItem} />
            </div>
          </div>
          <div className="w-1/3 min-h-0">{aside}</div>
        </div>
      </div>
    </LanguageContext.Provider>
  </DndContext>
}

interface GroupProps {
  items: PlaybookItem[]
  vars: string[]
  groups: FlowGroups
  issues: PlaybookIssue[]
  activeId?: string
  flashId?: string
  onUpdate: (index: number, patch: Partial<PlaybookItem>) => void
  onRemove: (index: number) => void
}

function ItemGroup({ items, vars, groups, issues, activeId, flashId, onUpdate, onRemove }: GroupProps) {
  return <div className="flex flex-col gap-2">
    <InsertGap index={0} />
    {items.map((item, index) => {
      const Card = item.type === "ai" ? AiItemCard : ItemCard
      return <Fragment key={item.id || index}>
        <div data-item-index={index} className={cn("rounded-lg transition-shadow", activeId && item.id === activeId && "ring-2 ring-primary ring-offset-2", flashId && item.id === flashId && "ring-2 ring-amber-400 ring-offset-2")}>
          <Card item={item} vars={vars} groups={groups} index={index} issues={issues.filter(i => i.index === index)} onUpdate={patch => onUpdate(index, patch)} onRemove={() => onRemove(index)} />
        </div>
        <InsertGap index={index + 1} />
      </Fragment>
    })}
  </div>
}

function InsertGap({ index }: { index: number }) {
  const { setNodeRef, isOver } = useDroppable({ id: `gap-${index}` })
  return <div ref={setNodeRef} className={cn("rounded transition-all", isOver ? "h-8 bg-primary/20 border-2 border-dashed border-primary" : "h-0")} />
}
